import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { Agreement, AgreementPage, AgreementPageCheckBox } from '../../types/agreement';
import { bmxurl } from '../../globals';
import { GlobalService } from '../_services/global.service';
import { BusyService } from '../_services/busy.service';

@Component({
  selector: 'app-agreement',
  templateUrl: './agreement.component.html',
  styleUrls: ['./agreement.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AgreementComponent implements OnInit {

  agreement: Agreement | null = null;
  currentPageIndex: number = 0;
  currentPage: any | null = null;
  isLoading: boolean = true;
  agreementId: string = '';
  agreementType: string = '';
  returnAction: string = '';
  VenueID: string = localStorage.getItem("SmartVenueID")!;
  isLastPage: boolean = false;
  isFirstPage: boolean = true;
  allCheckboxesValid: boolean = false;
  httpResponse: any;
  StateOptions: any[];
  otpCode: string[] = ['', '', '', '', '', ''];
  ShowOTP: boolean = false;
  authMessage: string = "";

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private messageService: MessageService,
    private globalService: GlobalService,
    private busyService: BusyService
  ) { 
    this.StateOptions = [{label: 'No', value: false}, {label: 'Yes', value: true}];
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(qp => {
      this.returnAction = qp['returnAction'] || '';
    });
    this.route.params.subscribe(params => {
      this.agreementType = params['id'];
      if (this.agreementType) {
        this.loadAgreement();
      } else {
        this.messageService.add({severity: 'error', summary: 'Error', detail: 'No agreement ID provided'});
        this.router.navigate(['/']);
      }
    });
  }

  loadAgreement(): void {
  
    this.isLoading = true;
    this.busyService.busy();

    this.http.get(bmxurl + "AgreementLatest/" + this.VenueID + "/" + this.agreementType).subscribe(response => {
        this.httpResponse =  (response as any)
        if(this.httpResponse.succeeded)
        {
          this.agreement = this.httpResponse.data as any;
          const venueName = localStorage.getItem("SmartVenueName") || "";
          this.agreement.pages.forEach(page => {
            page.pagetext = page.pagetext
              .replace(/\[\[VENUENAME\]\]/g, venueName)
              .replace(/\[VENUE NAME\]/g, venueName);
            page.checkboxes.forEach(cb => {
              cb.text = cb.text
                .replace(/\[\[VENUENAME\]\]/g, venueName)
                .replace(/\[VENUE NAME\]/g, venueName);
            });
          });
          this.currentPage = this.agreement.pages[0];


          this.updatePageStatus();
          this.validateCurrentPage();

          this.agreementId = this.agreement.agreementid;

          this.authMessage = this.agreement.authorizationmessage.replace("[[TODAYSDATE]]", new Date().toLocaleDateString());
          this.authMessage = this.authMessage.replace("[[EXPIRYDATE]]", new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toLocaleDateString());
          this.authMessage = this.authMessage.replace(/\[\[VENUENAME\]\]/g, venueName).replace(/\[VENUE NAME\]/g, venueName);

           this.busyService.idle();
          this.isLoading = false;
        }
    });

  }

  updateReplacements(currentPageText: string): string {
    const venueName = localStorage.getItem("SmartVenueName") || "";
    return currentPageText
      .replace(/\[\[VENUENAME\]\]/g, venueName)
      .replace(/\[VENUE NAME\]/g, venueName);
  }

  updatePageStatus(): void {
    if (this.agreement) {
      this.isFirstPage = this.currentPageIndex === 0;
      this.isLastPage = this.currentPageIndex === this.agreement.pages.length - 1;
    }
  }

  onSelectChange(checkbox: any): void {
    checkbox.value = !checkbox.value;
    this.validateCurrentPage();
  }

  validateCurrentPage(): boolean {
    if (!this.currentPage) {
      this.allCheckboxesValid = false;
      return false;
    }

    this.allCheckboxesValid = this.currentPage.checkboxes.every(checkbox => checkbox.value === true);
    return this.allCheckboxesValid;
  }

  nextPage(): void {
    if (!this.validateCurrentPage()) {
      this.messageService.add({
        severity: 'warn', 
        summary: 'Required', 
        detail: 'Please check all boxes before continuing'
      });
      return;
    }

    if (this.agreement && this.currentPageIndex < this.agreement.pages.length - 1) {
      this.currentPageIndex++;
      this.currentPage = this.agreement.pages[this.currentPageIndex];
      this.updatePageStatus();
      this.validateCurrentPage();
      
      setTimeout(() => {
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }, 0);
    }
  }

  previousPage(): void {
    if (this.agreement && this.currentPageIndex > 0) {
      this.currentPageIndex--;
      this.currentPage = this.agreement.pages[this.currentPageIndex];
      this.updatePageStatus();
      this.validateCurrentPage();

      setTimeout(() => {
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }, 0);
    }
  }

  saveAgreement(): void {
    this.busyService.busy();
    let OPTRequest= {
      venueid: this.VenueID,
      module : this.agreementId,
    }

    this.http.post(bmxurl + "otp", OPTRequest).subscribe(response => {
      this.httpResponse = response
      if(this.httpResponse.succeeded)
      {
        this.ShowOTP = true;

        setTimeout(() => {
          const firstInput = document.querySelector('.otp-input') as HTMLInputElement; 
          if (firstInput) {
            firstInput.focus();
          }
        }, 0);

        this.busyService.idle();
      }
      else
      {
        this.messageService.add({severity:'error', summary:'Error', detail:'Error while requesting OTP, error:' + this.httpResponse.messages[0]});
        this.busyService.idle();
      }
      }, error => {
        this.messageService.add({severity:'error', summary:'Error', detail:'Error while requesting OTP, error:' + error.messages[0]});
        this.busyService.idle();
    });
    
  }

  getPageProgress(): number {
    if (!this.agreement) return 0;
    return Math.round(((this.currentPageIndex + 1) / this.agreement.pages.length) * 100);
  }

  getPageText(): string {
    if (!this.agreement) return '';
    
    return `Page ${this.currentPageIndex + 1} of ${this.agreement.pages.length}`;
  }

  submitOtpCode(){

    const enteredCode = this.getOtpValue();
    this.ShowOTP = false;
    const otpHeaders = new HttpHeaders().set('otp', enteredCode);

    this.http.post<any>(bmxurl + "/AgreementAnswer", this.agreement, { headers: otpHeaders }).subscribe(response => {
      if(response.succeeded)
      {
        this.httpResponse = response;

        this.messageService.add({severity:'success', summary:'Saved', detail:'Agreement successfully saved'});
        this.clearOtpInputs();

        if (this.returnAction) {
          this.router.navigate(['/venuesettings'], { queryParams: { action: this.returnAction } });
        } else {
          this.router.navigate(['/venuesettings']);
        }
      }
      else
      {
        this.messageService.add({severity:'error', summary:'Not Saved', detail:response.messages[0]});
        this.clearOtpInputs();
      }

    }, error => {
      this.messageService.add({severity:'error', summary:'Not Saved', detail:error.messages[0]});
      this.clearOtpInputs();  
   })
  }
  
  clearOtpInputs() {
    this.otpCode = ['', '', '', '', '', ''];
    const inputs = document.querySelectorAll('.otp-input');
    inputs.forEach(input => (input as HTMLInputElement).value = '');
  }

  onOtpInput(event: any, index: number) {
    const input = event.target;
    const value = input.value.toUpperCase();
    
    // Only allow alphanumeric characters
    if (!/^[A-Z0-9]$/.test(value) && value !== '') {
      input.value = '';
      this.otpCode[index] = '';
      return;
    }
    
    this.otpCode[index] = value;
    
    // Move to next input if current input is filled
    if (value && index < 5) {
      const inputs = document.querySelectorAll('.otp-input');
      if (inputs[index + 1]) {
        (inputs[index + 1] as HTMLInputElement).focus();
      }
    }
  }
  
  onOtpKeydown(event: any, index: number) {
    // Handle backspace
    if (event.key === 'Backspace' && !this.otpCode[index] && index > 0) {
      const inputs = document.querySelectorAll('.otp-input');
      if (inputs[index - 1]) {
        (inputs[index - 1] as HTMLInputElement).focus();
      }
    }
  }
  
  getOtpValue(): string {
    return this.otpCode.join('');
  }

  onOtpPaste(event: any, index: number) {
    event.preventDefault();
    
    // Get pasted text
    const pasteData = (event.clipboardData || (window as any).clipboardData).getData('text');
    
    // Remove any non-alphanumeric characters (including hyphens)
    const cleanedData = pasteData.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    
    // Only process if we have exactly 6 characters
    if (cleanedData.length === 6) {
      // Fill all OTP fields
      for (let i = 0; i < 6; i++) {
        this.otpCode[i] = cleanedData[i] || '';
      }
      
      // Focus the last input field
      setTimeout(() => {
        const inputs = document.querySelectorAll('.otp-input');
        if (inputs[5]) {
          (inputs[5] as HTMLInputElement).focus();
        }
      }, 10);
    } else if (cleanedData.length > 0) {
      // If less than 6 characters, fill from current position
      const remainingSlots = 6 - index;
      const charsToFill = Math.min(cleanedData.length, remainingSlots);
      
      for (let i = 0; i < charsToFill; i++) {
        this.otpCode[index + i] = cleanedData[i] || '';
      }
      
      // Focus the next empty field or last field
      setTimeout(() => {
        const nextIndex = Math.min(index + charsToFill, 5);
        const inputs = document.querySelectorAll('.otp-input');
        if (inputs[nextIndex]) {
          (inputs[nextIndex] as HTMLInputElement).focus();
        }
      }, 10);
    }
  }
}
